// oxlint-disable-next-line no-restricted-globals
const channel = new BroadcastChannel("myvp/sync-session-storage")

/**
 * @module
 * This module allows syncing writes (not reads) to sessionStorage across multiple tabs with the same domain.
 *
 * This solution does not guarantee that the data in each sessionStorage is the same in all cases.
 */
export const setupSessionStorageSync = (args?: {
  updateHandlers?: Record<
    string,
    undefined | ((previousValue: string | null, b: string) => string)
  >
}) => {
  const abortController = new AbortController()
  channel.addEventListener(
    "message",
    (event) => {
      if (isSessionStorageSyncMessage(event.data)) {
        const handler = args?.updateHandlers?.[event.data.key]
        if (handler && typeof handler === "function") {
          const previousValue = sessionStorage.getItem(event.data.key)
          const updatedValue = handler(previousValue, event.data.value)
          sessionStorage.setItem(event.data.key, updatedValue)
        } else {
          sessionStorage.setItem(event.data.key, event.data.value)
        }
      }
    },
    { signal: abortController.signal }
  )
  return {
    channel,
    unsubscribe: () => {
      abortController.abort()
    },
  }
}

interface SessionStorageSyncMessage {
  type: "sessionStorageSync"
  key: string
  value: string
}

const isSessionStorageSyncMessage = (
  message: unknown
): message is SessionStorageSyncMessage => {
  return Boolean(
    message &&
    typeof message === "object" &&
    "type" in message &&
    message.type === "sessionStorageSync" &&
    "key" in message &&
    typeof message.key === "string" &&
    "value" in message &&
    typeof message.value === "string"
  )
}

export const dispatchStorageSyncMessage = (key: string, value: string) => {
  const message: SessionStorageSyncMessage = {
    type: "sessionStorageSync",
    key,
    value,
  }
  channel.postMessage(message)
}
